/**
 * Single source for the public, crawlable copy of deenly.life.
 *
 * vite.config.js renders the English version into index.html at build time
 * (meta tags, JSON-LD and the no-JS fallback inside #app), and the landing
 * page sections read the same objects, so what crawlers see without
 * JavaScript, what the structured data claims and what people see in the app
 * never drift apart. Keep public/llms.txt in step when this changes.
 */

import { QURAN_LOCALES } from '../utils/quran/locale.js';

export const SITE_URL = 'https://deenly.life';
export const APP_STORE_URL = 'https://apps.apple.com/us/app/deenly/id1608171212';
export const LOGO_URL = `${SITE_URL}/deenly-logo.jpg`;

/**
 * App Store link tagged with a campaign token, so App Store Connect →
 * App Analytics → Sources shows which page sent the download.
 */
export const appStoreUrl = (campaign) => `${APP_STORE_URL}?ct=${encodeURIComponent(campaign)}&mt=8`;

export const meta = {
  title: 'Deenly — Read the Quran Online, Prayer Times & Quran App for iPhone',
  description:
    'Read the whole Quran free online in Arabic, English and Bosnian, with audio, search and word statistics. Plus the Deenly iPhone app: prayer times, mosque finder, AI tajweed and live Khutbah translation.',
};

export const siteContent = {
  en: {
    whatIs: {
      badge: 'What is Deenly',
      title: 'What is Deenly?',
      audience:
        'Deenly is a free iPhone and iPad app for Muslims who want prayer times, the Quran, nearby mosques and trustworthy answers about Islam in one place — whether you are learning the basics, memorising the Quran or raising young children.',
      features: [
        {
          icon: 'solar:book-bookmark-linear',
          title: 'The Quran online, free for everyone',
          body: 'All 114 surahs on deenly.life in Arabic with English and Bosnian translation, verse-by-verse audio, search by word or meaning, and word statistics. No account needed.',
        },
        {
          icon: 'solar:clock-circle-linear',
          title: 'Prayer times & adhan',
          body: 'Accurate salah times for your location, adhan reminders, widgets and a Live Activity on the lock screen.',
        },
        {
          icon: 'solar:map-point-linear',
          title: 'Mosque finder',
          body: 'Nearby mosques with navigation, and a plain answer to whether you can still make the next prayer there.',
        },
        {
          icon: 'solar:book-2-linear',
          title: 'Quran reading',
          body: 'Seven Arabic script styles, translation, audio, bookmarks and a khatm planner.',
        },
        {
          icon: 'solar:microphone-3-linear',
          title: 'AI tajweed & Hifz AI',
          body: 'Recite aloud and get word-by-word feedback. The speech model runs on your phone, works offline and your voice never leaves the device.',
        },
        {
          icon: 'solar:chat-round-dots-linear',
          title: 'Ask Deenly',
          body: 'Answers about Islam built only from the Quran and authenticated hadith, with the source under every quote.',
        },
        {
          icon: 'solar:translation-2-linear',
          title: 'Live Khutbah translation',
          body: 'Read the Friday sermon in your language in real time — 6 spoken languages in, 15 languages out.',
        },
        {
          icon: 'solar:soundwave-linear',
          title: 'Ayah finder & Mushaf lens',
          body: 'Recognise a recited verse in seconds, or point the camera at a printed Quran page to open that ayah.',
        },
        {
          icon: 'solar:moon-sleep-linear',
          title: 'Dream insights',
          body: 'A structured reflection on your dream based on the Quran, Sunnah and classical scholars such as Ibn Sirin.',
        },
        {
          icon: 'solar:compass-linear',
          title: 'Qibla with AR',
          body: 'A qibla compass with an augmented-reality view of the Kaaba direction.',
        },
        {
          icon: 'solar:calculator-linear',
          title: 'Everyday Islamic tools',
          body: 'Tasbih counter, zakat and inheritance (faraid) calculators, a halal barcode scanner and a salah posture guide.',
        },
        {
          icon: 'solar:star-fall-minimalistic-2-linear',
          title: 'Deenly Kids',
          body: 'Storybooks, nasheeds without instruments, calm sounds and educational games behind a parent PIN.',
        },
        {
          icon: 'solar:library-linear',
          title: 'Islamic library',
          body: 'Books, hadith collections, lectures and podcasts explaining classical texts.',
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Frequently asked questions',
      items: [
        {
          q: 'What is Deenly?',
          a: 'Deenly is an iOS app for daily Muslim worship. It brings together prayer times, a mosque finder, Quran reading with AI tajweed feedback, Ask Deenly answers from the Quran and Sunnah, live Khutbah translation, dream insights, qibla with AR, zakat and tasbih tools, and Deenly Kids for children.',
        },
        {
          q: 'Can I read the Quran online for free?',
          a: 'Yes. The whole Quran is open on deenly.life/quran: all 114 surahs in Arabic with English (Saheeh International) and Bosnian (Besim Korkut) translation and verse-by-verse recitation by six reciters. No account, no ads.',
        },
        {
          q: 'How many times is a word mentioned in the Quran?',
          a: 'Deenly counts it for you. On deenly.life/quran/words type any word in Arabic, English or Bosnian to see how many times it occurs, in how many ayat and surahs, Meccan or Medinan, and every ayah where it appears. You can also compare two words.',
        },
        {
          q: 'What is the best app for prayer times and Quran?',
          a: 'If you want prayer times and the Quran in one app, Deenly is built for exactly that. It gives accurate salah times with adhan reminders and widgets, a full Quran with translation, audio and bookmarks, and AI that listens to your recitation and points out tajweed mistakes — plus a mosque finder and live Khutbah translation. It is free to download on the App Store and has no ads.',
        },
        {
          q: 'Is Deenly free?',
          a: 'Yes. Deenly is free to download and has no ads. Optional Deenly Premium unlocks the advanced AI features, with a 7-day free trial and monthly, yearly or one-time lifetime options.',
        },
        {
          q: 'Is Deenly available on Android?',
          a: 'Deenly is currently available for iPhone and iPad on the App Store (iOS 18 or later).',
        },
        {
          q: 'How does live Khutbah translation work?',
          a: 'Point your phone at the imam and Deenly shows the sermon in your language line by line, with Quran verses in Arabic. An imam can also broadcast with a room code so the whole congregation follows on their own phones. It understands 6 spoken languages and translates into 15.',
        },
        {
          q: 'Where do Ask Deenly answers come from?',
          a: 'Ask Deenly builds its answers only from the Quran and authenticated hadith collections and shows the source under every quote. When there is no evidence, it says so. It is a knowledge aid, not a fatwa.',
        },
        {
          q: 'Can Deenly help me memorise the Quran?',
          a: 'Yes. With Hifz AI you hide the ayah, recite it aloud, and Deenly marks every word it heard as right or wrong. The speech model runs on your phone, so it works offline and your voice never leaves the device. Daily review helps you keep what you have learned.',
        },
        {
          q: 'Is Deenly safe for children?',
          a: 'Deenly Kids is a separate space with storybooks, nasheeds without instruments, calm sounds and educational games. It is locked behind a parent PIN, with no ads and no outside links.',
        },
        {
          q: 'Which languages does Deenly support?',
          a: 'The app is available in English and Bosnian. Live Khutbah translation supports 15 output languages.',
        },
      ],
    },
  },

  bs: {
    whatIs: {
      badge: 'Šta je Deenly',
      title: 'Šta je Deenly?',
      audience:
        'Deenly je besplatna aplikacija za iPhone i iPad namijenjena muslimanima koji žele vaktiju, Kur’an, obližnje džamije i pouzdane odgovore o islamu na jednom mjestu — bilo da tek učite osnove, učite Kur’an napamet ili odgajate malu djecu.',
      features: [
        {
          icon: 'solar:book-bookmark-linear',
          title: 'Kur’an online, besplatno za sve',
          body: 'Svih 114 sura na deenly.life na arapskom, s prijevodom Besima Korkuta i engleskim prijevodom, audio ajet po ajet, pretraga po riječi ili značenju i statistika riječi. Bez prijave.',
        },
        {
          icon: 'solar:clock-circle-linear',
          title: 'Vaktija i ezan',
          body: 'Tačna vremena namaza za vašu lokaciju, podsjetnici za ezan, widgeti i Live Activity na zaključanom ekranu.',
        },
        {
          icon: 'solar:map-point-linear',
          title: 'Pronalazač džamija',
          body: 'Obližnje džamije s navigacijom i jasnim odgovorom da li stižete na sljedeći namaz.',
        },
        {
          icon: 'solar:book-2-linear',
          title: 'Čitanje Kur’ana',
          body: 'Sedam stilova arapskog pisma, prijevod, audio, zabilješke i planer hatme.',
        },
        {
          icon: 'solar:microphone-3-linear',
          title: 'AI tedžvid i Hifz AI',
          body: 'Proučite naglas i dobijte povratnu informaciju za svaku riječ. Model radi na telefonu, bez interneta, i vaš glas ne napušta uređaj.',
        },
        {
          icon: 'solar:chat-round-dots-linear',
          title: 'Pitaj Deenly',
          body: 'Odgovori o islamu izgrađeni isključivo iz Kur’ana i vjerodostojnih hadisa, s izvorom ispod svakog citata.',
        },
        {
          icon: 'solar:translation-2-linear',
          title: 'Prijevod hutbe uživo',
          body: 'Pratite džumansku hutbu na svom jeziku u realnom vremenu — 6 jezika govora, 15 jezika prijevoda.',
        },
        {
          icon: 'solar:soundwave-linear',
          title: 'Prepoznavanje ajeta i Mushaf lens',
          body: 'Prepoznajte proučeni ajet za par sekundi ili usmjerite kameru na stranicu Mushafa.',
        },
        {
          icon: 'solar:moon-sleep-linear',
          title: 'Tumačenje snova',
          body: 'Strukturisano razmišljanje o snu na osnovu Kur’ana, sunneta i klasičnih učenjaka poput Ibn Sirina.',
        },
        {
          icon: 'solar:compass-linear',
          title: 'Kibla s AR prikazom',
          body: 'Kompas za kiblu s prikazom smjera Kabe u proširenoj stvarnosti.',
        },
        {
          icon: 'solar:calculator-linear',
          title: 'Svakodnevni islamski alati',
          body: 'Tesbih, kalkulatori zekata i nasljedstva (feraid), halal skener barkoda i vodič za položaje u namazu.',
        },
        {
          icon: 'solar:star-fall-minimalistic-2-linear',
          title: 'Deenly Kids',
          body: 'Slikovnice, ilahije bez instrumenata, umirujući zvukovi i edukativne igre iza roditeljskog PIN-a.',
        },
        {
          icon: 'solar:library-linear',
          title: 'Islamska biblioteka',
          body: 'Knjige, zbirke hadisa, predavanja i podcasti koji objašnjavaju klasična djela.',
        },
      ],
    },
    faq: {
      badge: 'Pitanja',
      title: 'Često postavljana pitanja',
      items: [
        {
          q: 'Šta je Deenly?',
          a: 'Deenly je iOS aplikacija za svakodnevni ibadet. Objedinjuje vaktiju, pronalazač džamija, čitanje Kur’ana s AI povratnom informacijom o tedžvidu, odgovore Pitaj Deenly iz Kur’ana i sunneta, prijevod hutbe uživo, tumačenje snova, kiblu s AR prikazom, zekat i tesbih te Deenly Kids za djecu.',
        },
        {
          q: 'Mogu li čitati Kur’an online besplatno?',
          a: 'Da. Cijeli Kur’an je otvoren na deenly.life/quran: svih 114 sura na arapskom, s prijevodom Besima Korkuta i engleskim prijevodom, i učenjem ajet po ajet šest učača. Bez prijave i bez reklama.',
        },
        {
          q: 'Koliko puta se neka riječ spominje u Kur’anu?',
          a: 'Deenly to prebroji za vas. Na deenly.life/quran/words upišite bilo koju riječ na arapskom, bosanskom ili engleskom i vidite koliko se puta pojavljuje, u koliko ajeta i sura, mekanskih ili medinskih, i svaki ajet u kojem je. Možete uporediti i dvije riječi.',
        },
        {
          q: 'Koja je najbolja aplikacija za vaktiju i Kur’an?',
          a: 'Ako želite vaktiju i Kur’an u jednoj aplikaciji, Deenly je napravljen upravo za to: tačna vremena namaza s podsjetnicima i widgetima, cijeli Kur’an s prijevodom, audiom i zabilješkama, i AI koji sluša vaše učenje i ukazuje na greške u tedžvidu — uz pronalazač džamija i prijevod hutbe uživo. Besplatna je na App Storeu i nema reklama.',
        },
        {
          q: 'Da li je Deenly besplatan?',
          a: 'Da. Deenly je besplatan za preuzimanje i nema reklama. Opcionalni Deenly Premium otključava napredne AI funkcije, uz 7 dana besplatne probe i mjesečnu, godišnju ili jednokratnu doživotnu opciju.',
        },
        {
          q: 'Da li je Deenly dostupan na Androidu?',
          a: 'Deenly je trenutno dostupan za iPhone i iPad na App Storeu (iOS 18 ili noviji).',
        },
        {
          q: 'Kako radi prijevod hutbe uživo?',
          a: 'Usmjerite telefon prema imamu i Deenly prikazuje hutbu na vašem jeziku red po red, s ajetima na arapskom. Imam može i emitovati putem koda sobe, pa cijeli džemat prati na svojim telefonima. Razumije 6 jezika govora i prevodi na 15.',
        },
        {
          q: 'Odakle dolaze odgovori Pitaj Deenly?',
          a: 'Pitaj Deenly gradi odgovore isključivo iz Kur’ana i vjerodostojnih zbirki hadisa i prikazuje izvor ispod svakog citata. Kada dokaza nema, to i kaže. To je pomoć u znanju, a ne fetva.',
        },
        {
          q: 'Može li mi Deenly pomoći da učim Kur’an napamet?',
          a: 'Da. U Hifz AI sakrijete ajet, proučite ga naglas, a Deenly označi svaku riječ koju je čuo kao tačnu ili netačnu. Model radi na telefonu, pa radi bez interneta i vaš glas ne napušta uređaj. Dnevno ponavljanje pomaže da zadržite naučeno.',
        },
        {
          q: 'Da li je Deenly siguran za djecu?',
          a: 'Deenly Kids je poseban prostor sa slikovnicama, ilahijama bez instrumenata, umirujućim zvukovima i edukativnim igrama. Zaključan je roditeljskim PIN-om, bez reklama i bez vanjskih linkova.',
        },
        {
          q: 'Koje jezike Deenly podržava?',
          a: 'Aplikacija je dostupna na engleskom i bosanskom. Prijevod hutbe uživo podržava 15 jezika prijevoda.',
        },
      ],
    },
  },

  de: {
    whatIs: {
      badge: 'Was ist Deenly',
      title: 'Was ist Deenly?',
      audience:
        'Deenly ist eine kostenlose App für iPhone und iPad für Muslime, die Gebetszeiten, den Koran, Moscheen in der Nähe und verlässliche Antworten zum Islam an einem Ort haben möchten, ob du gerade die Grundlagen lernst, den Koran auswendig lernst oder kleine Kinder großziehst.',
      features: [
        {
          icon: 'solar:book-bookmark-linear',
          title: 'Der Koran online, kostenlos für alle',
          body: 'Alle 114 Suren auf deenly.life auf Arabisch mit deutscher Übersetzung, Audio Vers für Vers, Suche nach Wort oder Bedeutung und Wortstatistik. Ohne Anmeldung.',
        },
        {
          icon: 'solar:clock-circle-linear',
          title: 'Gebetszeiten & Adhan',
          body: 'Genaue Gebetszeiten für deinen Standort, Adhan-Erinnerungen, Widgets und eine Live-Aktivität auf dem Sperrbildschirm.',
        },
        {
          icon: 'solar:map-point-linear',
          title: 'Moscheefinder',
          body: 'Moscheen in der Nähe mit Navigation und einer klaren Antwort darauf, ob du das nächste Gebet dort noch schaffst.',
        },
        {
          icon: 'solar:book-2-linear',
          title: 'Koran lesen',
          body: 'Sieben arabische Schriftarten, Übersetzung, Audio, Lesezeichen und ein Khatm-Planer.',
        },
        {
          icon: 'solar:microphone-3-linear',
          title: 'Tajwid mit KI & Hifz AI',
          body: 'Rezitiere laut und erhalte Rückmeldung zu jedem Wort. Das Sprachmodell läuft auf deinem Handy, funktioniert offline, und deine Stimme verlässt das Gerät nie.',
        },
        {
          icon: 'solar:chat-round-dots-linear',
          title: 'Ask Deenly',
          body: 'Antworten zum Islam, die nur auf dem Koran und authentischen Hadithen beruhen, mit der Quelle unter jedem Zitat.',
        },
        {
          icon: 'solar:translation-2-linear',
          title: 'Live-Übersetzung der Khutba',
          body: 'Lies die Freitagspredigt in Echtzeit in deiner Sprache: 6 gesprochene Sprachen, 15 Zielsprachen.',
        },
        {
          icon: 'solar:soundwave-linear',
          title: 'Verserkennung & Mushaf-Linse',
          body: 'Erkenne einen rezitierten Vers in Sekunden, oder richte die Kamera auf eine gedruckte Koranseite, um diesen Vers zu öffnen.',
        },
        {
          icon: 'solar:moon-sleep-linear',
          title: 'Traumdeutung',
          body: 'Eine strukturierte Betrachtung deines Traums auf Grundlage von Koran, Sunna und klassischen Gelehrten wie Ibn Sirin.',
        },
        {
          icon: 'solar:compass-linear',
          title: 'Qibla mit AR',
          body: 'Ein Qibla-Kompass mit Augmented-Reality-Ansicht der Richtung zur Kaaba.',
        },
        {
          icon: 'solar:calculator-linear',
          title: 'Islamische Werkzeuge für jeden Tag',
          body: 'Tasbih-Zähler, Rechner für Zakat und Erbrecht (Faraid), ein Halal-Barcode-Scanner und eine Anleitung zu den Gebetshaltungen.',
        },
        {
          icon: 'solar:star-fall-minimalistic-2-linear',
          title: 'Deenly Kids',
          body: 'Bilderbücher, Naschids ohne Instrumente, ruhige Klänge und Lernspiele hinter einer Eltern-PIN.',
        },
        {
          icon: 'solar:library-linear',
          title: 'Islamische Bibliothek',
          body: 'Bücher, Hadith-Sammlungen, Vorträge und Podcasts, die klassische Texte erklären.',
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Häufig gestellte Fragen',
      items: [
        {
          q: 'Was ist Deenly?',
          a: 'Deenly ist eine iOS-App für den täglichen Gottesdienst. Sie vereint Gebetszeiten, einen Moscheefinder, das Lesen des Korans mit KI-Rückmeldung zum Tajwid, Antworten von Ask Deenly aus Koran und Sunna, die Live-Übersetzung der Khutba, Traumdeutung, Qibla mit AR, Zakat- und Tasbih-Werkzeuge sowie Deenly Kids für Kinder.',
        },
        {
          q: 'Kann ich den Koran kostenlos online lesen?',
          a: 'Ja. Der ganze Koran ist auf deenly.life/de/quran frei zugänglich: alle 114 Suren auf Arabisch mit der deutschen Übersetzung von Frank Bubenheim & Nadeem Elyas und Rezitation Vers für Vers von sechs Rezitatoren. Ohne Anmeldung, ohne Werbung.',
        },
        {
          q: 'Wie oft wird ein Wort im Koran erwähnt?',
          a: 'Deenly zählt es für dich. Gib auf deenly.life/de/quran/words ein beliebiges Wort auf Arabisch, Deutsch oder Englisch ein und sieh, wie oft es vorkommt, in wie vielen Versen und Suren, mekkanisch oder medinensisch, und jeden Vers, in dem es steht. Du kannst auch zwei Wörter vergleichen.',
        },
        {
          q: 'Was ist die beste App für Gebetszeiten und Koran?',
          a: 'Wenn du Gebetszeiten und den Koran in einer App möchtest, ist Deenly genau dafür gemacht: genaue Gebetszeiten mit Adhan-Erinnerungen und Widgets, der ganze Koran mit Übersetzung, Audio und Lesezeichen und eine KI, die deine Rezitation anhört und auf Tajwid-Fehler hinweist, dazu ein Moscheefinder und die Live-Übersetzung der Khutba. Der Download im App Store ist kostenlos, und es gibt keine Werbung.',
        },
        {
          q: 'Ist Deenly kostenlos?',
          a: 'Ja. Deenly kann kostenlos geladen werden und hat keine Werbung. Das optionale Deenly Premium schaltet die erweiterten KI-Funktionen frei, mit 7 Tagen kostenloser Testphase und monatlicher, jährlicher oder einmaliger lebenslanger Option.',
        },
        {
          q: 'Gibt es Deenly für Android?',
          a: 'Deenly ist derzeit für iPhone und iPad im App Store verfügbar (iOS 18 oder neuer).',
        },
        {
          q: 'Wie funktioniert die Live-Übersetzung der Khutba?',
          a: 'Richte dein Handy auf den Imam, und Deenly zeigt die Predigt Zeile für Zeile in deiner Sprache, mit Koranversen auf Arabisch. Ein Imam kann auch mit einem Raumcode senden, sodass die ganze Gemeinde auf dem eigenen Handy mitliest. Die App versteht 6 gesprochene Sprachen und übersetzt in 15.',
        },
        {
          q: 'Woher kommen die Antworten von Ask Deenly?',
          a: 'Ask Deenly bildet seine Antworten nur aus dem Koran und authentischen Hadith-Sammlungen und zeigt die Quelle unter jedem Zitat. Gibt es keinen Beleg, sagt es das auch. Es ist eine Wissenshilfe, keine Fatwa.',
        },
        {
          q: 'Kann Deenly mir helfen, den Koran auswendig zu lernen?',
          a: 'Ja. Mit Hifz AI blendest du den Vers aus, rezitierst ihn laut, und Deenly markiert jedes gehörte Wort als richtig oder falsch. Das Sprachmodell läuft auf deinem Handy, also funktioniert es offline, und deine Stimme verlässt das Gerät nie. Die tägliche Wiederholung hilft dir, das Gelernte zu behalten.',
        },
        {
          q: 'Ist Deenly sicher für Kinder?',
          a: 'Deenly Kids ist ein eigener Bereich mit Bilderbüchern, Naschids ohne Instrumente, ruhigen Klängen und Lernspielen. Er ist mit einer Eltern-PIN gesichert, ohne Werbung und ohne externe Links.',
        },
        {
          q: 'Welche Sprachen unterstützt Deenly?',
          a: 'Die App gibt es auf Englisch und Bosnisch; der Koran auf deenly.life ist auch auf Deutsch, Niederländisch und Französisch verfügbar. Die Live-Übersetzung der Khutba unterstützt 15 Zielsprachen.',
        },
      ],
    },
  },

  nl: {
    whatIs: {
      badge: 'Wat is Deenly',
      title: 'Wat is Deenly?',
      audience:
        'Deenly is een gratis app voor iPhone en iPad voor moslims die gebedstijden, de Koran, moskeeën in de buurt en betrouwbare antwoorden over de islam op één plek willen, of je nu de basis leert, de Koran uit het hoofd leert of jonge kinderen opvoedt.',
      features: [
        {
          icon: 'solar:book-bookmark-linear',
          title: 'De Koran online, gratis voor iedereen',
          body: 'Alle 114 soera’s op deenly.life in het Arabisch met Nederlandse vertaling, audio vers voor vers, zoeken op woord of betekenis en woordstatistieken. Geen account nodig.',
        },
        {
          icon: 'solar:clock-circle-linear',
          title: 'Gebedstijden & adhan',
          body: 'Nauwkeurige gebedstijden voor je locatie, adhan-herinneringen, widgets en een Live Activity op het vergrendelscherm.',
        },
        {
          icon: 'solar:map-point-linear',
          title: 'Moskeezoeker',
          body: 'Moskeeën in de buurt met navigatie en een duidelijk antwoord op de vraag of je het volgende gebed daar nog haalt.',
        },
        {
          icon: 'solar:book-2-linear',
          title: 'Koran lezen',
          body: 'Zeven Arabische schriftstijlen, vertaling, audio, bladwijzers en een khatm-planner.',
        },
        {
          icon: 'solar:microphone-3-linear',
          title: 'Tajweed met AI & Hifz AI',
          body: 'Reciteer hardop en krijg per woord feedback. Het spraakmodel draait op je telefoon, werkt offline en je stem verlaat je apparaat nooit.',
        },
        {
          icon: 'solar:chat-round-dots-linear',
          title: 'Ask Deenly',
          body: 'Antwoorden over de islam, alleen gebaseerd op de Koran en authentieke hadith, met de bron onder elk citaat.',
        },
        {
          icon: 'solar:translation-2-linear',
          title: 'Live vertaling van de khutbah',
          body: 'Lees de vrijdagspreek in realtime in je eigen taal: 6 gesproken talen, 15 vertaaltalen.',
        },
        {
          icon: 'solar:soundwave-linear',
          title: 'Verzenherkenning & Mushaf-lens',
          body: 'Herken een gereciteerd vers in seconden, of richt de camera op een gedrukte Koranpagina om dat vers te openen.',
        },
        {
          icon: 'solar:moon-sleep-linear',
          title: 'Droomuitleg',
          body: 'Een gestructureerde bezinning op je droom op basis van de Koran, de Soenna en klassieke geleerden zoals Ibn Sirin.',
        },
        {
          icon: 'solar:compass-linear',
          title: 'Qibla met AR',
          body: 'Een qibla-kompas met een augmented-reality-weergave van de richting van de Kaaba.',
        },
        {
          icon: 'solar:calculator-linear',
          title: 'Islamitische tools voor elke dag',
          body: 'Tasbih-teller, calculators voor zakat en erfenis (faraid), een halal-barcodescanner en een gids voor de gebedshoudingen.',
        },
        {
          icon: 'solar:star-fall-minimalistic-2-linear',
          title: 'Deenly Kids',
          body: 'Prentenboeken, nasheeds zonder instrumenten, rustgevende geluiden en educatieve spelletjes achter een ouderpincode.',
        },
        {
          icon: 'solar:library-linear',
          title: 'Islamitische bibliotheek',
          body: 'Boeken, hadithverzamelingen, lezingen en podcasts die klassieke teksten uitleggen.',
        },
      ],
    },
    faq: {
      badge: 'Veelgestelde vragen',
      title: 'Veelgestelde vragen',
      items: [
        {
          q: 'Wat is Deenly?',
          a: 'Deenly is een iOS-app voor de dagelijkse aanbidding. De app brengt gebedstijden samen met een moskeezoeker, de Koran lezen met AI-feedback op je tajweed, antwoorden van Ask Deenly uit de Koran en de Soenna, live vertaling van de khutbah, droomuitleg, qibla met AR, tools voor zakat en tasbih, en Deenly Kids voor kinderen.',
        },
        {
          q: 'Kan ik de Koran gratis online lezen?',
          a: 'Ja. De hele Koran staat open op deenly.life/nl/quran: alle 114 soera’s in het Arabisch met de Nederlandse vertaling van het Rowwad Translation Center en recitatie vers voor vers door zes reciteurs. Geen account, geen advertenties.',
        },
        {
          q: 'Hoe vaak wordt een woord in de Koran genoemd?',
          a: 'Deenly telt het voor je. Typ op deenly.life/nl/quran/words een woord in het Arabisch, Nederlands of Engels en zie hoe vaak het voorkomt, in hoeveel verzen en soera’s, Mekkaans of Medinisch, en elk vers waarin het staat. Je kunt ook twee woorden vergelijken.',
        },
        {
          q: 'Wat is de beste app voor gebedstijden en de Koran?',
          a: 'Wil je gebedstijden en de Koran in één app, dan is Deenly daar precies voor gemaakt: nauwkeurige gebedstijden met adhan-herinneringen en widgets, de hele Koran met vertaling, audio en bladwijzers, en AI die naar je recitatie luistert en tajweedfouten aanwijst, plus een moskeezoeker en live vertaling van de khutbah. De app is gratis te downloaden in de App Store en heeft geen advertenties.',
        },
        {
          q: 'Is Deenly gratis?',
          a: 'Ja. Deenly is gratis te downloaden en heeft geen advertenties. Het optionele Deenly Premium ontgrendelt de geavanceerde AI-functies, met een gratis proefperiode van 7 dagen en een maandelijkse, jaarlijkse of eenmalige levenslange optie.',
        },
        {
          q: 'Is Deenly beschikbaar op Android?',
          a: 'Deenly is momenteel beschikbaar voor iPhone en iPad in de App Store (iOS 18 of nieuwer).',
        },
        {
          q: 'Hoe werkt de live vertaling van de khutbah?',
          a: 'Richt je telefoon op de imam en Deenly toont de preek regel voor regel in je eigen taal, met Koranverzen in het Arabisch. Een imam kan ook uitzenden met een kamercode, zodat de hele gemeente op de eigen telefoon meeleest. De app begrijpt 6 gesproken talen en vertaalt naar 15.',
        },
        {
          q: 'Waar komen de antwoorden van Ask Deenly vandaan?',
          a: 'Ask Deenly baseert zijn antwoorden alleen op de Koran en authentieke hadithverzamelingen en toont de bron onder elk citaat. Is er geen bewijs, dan zegt het dat ook. Het is een hulpmiddel bij kennis, geen fatwa.',
        },
        {
          q: 'Kan Deenly me helpen de Koran uit het hoofd te leren?',
          a: 'Ja. Met Hifz AI verberg je het vers, reciteer je het hardop en markeert Deenly elk gehoord woord als goed of fout. Het spraakmodel draait op je telefoon, dus het werkt offline en je stem verlaat je apparaat nooit. Dagelijks herhalen helpt je om te onthouden wat je hebt geleerd.',
        },
        {
          q: 'Is Deenly veilig voor kinderen?',
          a: 'Deenly Kids is een aparte ruimte met prentenboeken, nasheeds zonder instrumenten, rustgevende geluiden en educatieve spelletjes. Het is vergrendeld met een ouderpincode, zonder advertenties en zonder externe links.',
        },
        {
          q: 'Welke talen ondersteunt Deenly?',
          a: 'De app is beschikbaar in het Engels en Bosnisch; de Koran op deenly.life is ook beschikbaar in het Nederlands, Duits en Frans. De live vertaling van de khutbah ondersteunt 15 vertaaltalen.',
        },
      ],
    },
  },

  fr: {
    whatIs: {
      badge: 'Qu’est-ce que Deenly',
      title: 'Qu’est-ce que Deenly\u00a0?',
      audience:
        'Deenly est une application gratuite pour iPhone et iPad, pour les musulmans qui veulent réunir les heures de prière, le Coran, les mosquées proches et des réponses fiables sur l’islam, que tu apprennes les bases, que tu mémorises le Coran ou que tu élèves de jeunes enfants.',
      features: [
        {
          icon: 'solar:book-bookmark-linear',
          title: 'Le Coran en ligne, gratuit pour tous',
          body: 'Les 114 sourates sur deenly.life en arabe avec traduction française, l’audio verset par verset, la recherche par mot ou par sens et les statistiques de mots. Sans inscription.',
        },
        {
          icon: 'solar:clock-circle-linear',
          title: 'Heures de prière & adhan',
          body: 'Des heures de prière précises pour ton lieu, des rappels d’adhan, des widgets et une activité en direct sur l’écran verrouillé.',
        },
        {
          icon: 'solar:map-point-linear',
          title: 'Trouver une mosquée',
          body: 'Les mosquées proches avec navigation, et une réponse claire pour savoir si tu peux encore y faire la prochaine prière.',
        },
        {
          icon: 'solar:book-2-linear',
          title: 'Lire le Coran',
          body: 'Sept styles d’écriture arabe, traduction, audio, signets et planificateur de khatm.',
        },
        {
          icon: 'solar:microphone-3-linear',
          title: 'Tajwid par IA & Hifz AI',
          body: 'Récite à voix haute et reçois un retour mot par mot. Le modèle vocal fonctionne sur ton téléphone, hors ligne, et ta voix ne quitte jamais l’appareil.',
        },
        {
          icon: 'solar:chat-round-dots-linear',
          title: 'Ask Deenly',
          body: 'Des réponses sur l’islam tirées uniquement du Coran et des hadiths authentiques, avec la source sous chaque citation.',
        },
        {
          icon: 'solar:translation-2-linear',
          title: 'Khutba traduite en direct',
          body: 'Lis le sermon du vendredi dans ta langue en temps réel\u00a0: 6 langues parlées, 15 langues de traduction.',
        },
        {
          icon: 'solar:soundwave-linear',
          title: 'Reconnaissance de versets & objectif Mushaf',
          body: 'Reconnais un verset récité en quelques secondes, ou vise une page imprimée du Coran avec l’appareil photo pour ouvrir ce verset.',
        },
        {
          icon: 'solar:moon-sleep-linear',
          title: 'Interprétation des rêves',
          body: 'Une réflexion structurée sur ton rêve, fondée sur le Coran, la Sunna et des savants classiques comme Ibn Sirin.',
        },
        {
          icon: 'solar:compass-linear',
          title: 'Qibla en réalité augmentée',
          body: 'Une boussole de la qibla avec une vue en réalité augmentée de la direction de la Kaaba.',
        },
        {
          icon: 'solar:calculator-linear',
          title: 'Outils islamiques du quotidien',
          body: 'Compteur de tasbih, calculateurs de zakat et d’héritage (faraid), scanner de codes-barres halal et guide des positions de la prière.',
        },
        {
          icon: 'solar:star-fall-minimalistic-2-linear',
          title: 'Deenly Kids',
          body: 'Livres illustrés, nasheeds sans instruments, sons apaisants et jeux éducatifs derrière un code PIN parental.',
        },
        {
          icon: 'solar:library-linear',
          title: 'Bibliothèque islamique',
          body: 'Livres, recueils de hadiths, conférences et podcasts qui expliquent les textes classiques.',
        },
      ],
    },
    faq: {
      badge: 'FAQ',
      title: 'Questions fréquentes',
      items: [
        {
          q: 'Qu’est-ce que Deenly\u00a0?',
          a: 'Deenly est une application iOS pour la pratique quotidienne. Elle réunit les heures de prière, la recherche de mosquées, la lecture du Coran avec retour de l’IA sur le tajwid, les réponses d’Ask Deenly tirées du Coran et de la Sunna, la khutba traduite en direct, l’interprétation des rêves, la qibla en réalité augmentée, des outils de zakat et de tasbih, et Deenly Kids pour les enfants.',
        },
        {
          q: 'Puis-je lire le Coran en ligne gratuitement\u00a0?',
          a: 'Oui. Le Coran entier est ouvert sur deenly.life/fr/quran\u00a0: les 114 sourates en arabe avec la traduction française de Rachid Maach et la récitation verset par verset de six récitateurs. Sans inscription, sans publicité.',
        },
        {
          q: 'Combien de fois un mot est-il cité dans le Coran\u00a0?',
          a: 'Deenly le compte pour toi. Sur deenly.life/fr/quran/words, tape n’importe quel mot en arabe, en français ou en anglais et vois combien de fois il apparaît, dans combien de versets et de sourates, mecquoises ou médinoises, et chaque verset où il se trouve. Tu peux aussi comparer deux mots.',
        },
        {
          q: 'Quelle est la meilleure application pour les heures de prière et le Coran\u00a0?',
          a: 'Si tu veux les heures de prière et le Coran dans une seule application, Deenly est fait pour cela. Il donne des heures de prière précises avec rappels d’adhan et widgets, le Coran entier avec traduction, audio et signets, et une IA qui écoute ta récitation et signale les erreurs de tajwid, avec en plus la recherche de mosquées et la khutba traduite en direct. Il est gratuit sur l’App Store et sans publicité.',
        },
        {
          q: 'Deenly est-il gratuit\u00a0?',
          a: 'Oui. Deenly est gratuit à télécharger et sans publicité. L’option Deenly Premium débloque les fonctions d’IA avancées, avec 7 jours d’essai gratuit et une formule mensuelle, annuelle ou à vie en un seul paiement.',
        },
        {
          q: 'Deenly est-il disponible sur Android\u00a0?',
          a: 'Deenly est actuellement disponible pour iPhone et iPad sur l’App Store (iOS 18 ou version ultérieure).',
        },
        {
          q: 'Comment fonctionne la traduction de la khutba en direct\u00a0?',
          a: 'Oriente ton téléphone vers l’imam et Deenly affiche le sermon dans ta langue, ligne par ligne, avec les versets du Coran en arabe. Un imam peut aussi diffuser avec un code de salle, pour que toute l’assemblée suive sur son propre téléphone. L’application comprend 6 langues parlées et traduit vers 15.',
        },
        {
          q: 'D’où viennent les réponses d’Ask Deenly\u00a0?',
          a: 'Ask Deenly construit ses réponses uniquement à partir du Coran et de recueils de hadiths authentiques, et affiche la source sous chaque citation. Quand il n’y a pas de preuve, il le dit. C’est une aide à la connaissance, pas une fatwa.',
        },
        {
          q: 'Deenly peut-il m’aider à mémoriser le Coran\u00a0?',
          a: 'Oui. Avec Hifz AI, tu masques le verset, tu le récites à voix haute et Deenly marque chaque mot entendu comme juste ou faux. Le modèle vocal fonctionne sur ton téléphone, donc hors ligne, et ta voix ne quitte jamais l’appareil. La révision quotidienne t’aide à garder ce que tu as appris.',
        },
        {
          q: 'Deenly est-il sûr pour les enfants\u00a0?',
          a: 'Deenly Kids est un espace à part avec des livres illustrés, des nasheeds sans instruments, des sons apaisants et des jeux éducatifs. Il est verrouillé par un code PIN parental, sans publicité et sans liens externes.',
        },
        {
          q: 'Quelles langues Deenly prend-il en charge\u00a0?',
          a: 'L’application est disponible en anglais et en bosniaque\u00a0; le Coran sur deenly.life l’est aussi en français, en allemand et en néerlandais. La traduction de la khutba en direct prend en charge 15 langues.',
        },
      ],
    },
  },
};

/** schema.org graph for the home page, rendered into <head> as JSON-LD. */
export function buildJsonLd() {
  const { whatIs, faq } = siteContent.en;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Deenly',
        url: `${SITE_URL}/`,
        logo: { '@type': 'ImageObject', url: LOGO_URL, width: 1024, height: 1024 },
        sameAs: [APP_STORE_URL],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'Deenly',
        url: `${SITE_URL}/`,
        inLanguage: QURAN_LOCALES,
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${SITE_URL}/#app`,
        name: 'Deenly',
        alternateName: 'Deenly - Khutbah Translation',
        description: whatIs.audience,
        url: `${SITE_URL}/`,
        image: LOGO_URL,
        operatingSystem: 'iOS 18.0 or later',
        applicationCategory: 'ReferenceApplication',
        applicationSubCategory: 'Islamic prayer and Quran app',
        installUrl: APP_STORE_URL,
        downloadUrl: APP_STORE_URL,
        inLanguage: ['en', 'bs'],
        featureList: whatIs.features.map((f) => f.title),
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          url: APP_STORE_URL,
        },
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        mainEntity: faq.items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}
